import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Download, Pencil, Trash2, Search, Layers, Upload } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { API, authFetch } from '../utils/api';

export default function FleetManagement({ user }) {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [groupForm, setGroupForm] = useState({ name: '', price: '', cost: '' });
  const [formData, setFormData] = useState({
    unit_number: '',
    license_plate: '',
    make: '',
    model: '',
    customer_id: '',
    group_id: ''
  });
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const emptyRow = { unit_number: '', license_plate: '', make: '', model: '' };
  const [batchRows, setBatchRows] = useState([{ ...emptyRow }, { ...emptyRow }, { ...emptyRow }]);

  const location = useLocation();

  useEffect(() => {
    fetchVehicles();
    fetchCustomers();
    fetchGroups();
    const interval = setInterval(() => { fetchVehicles(); fetchCustomers(); fetchGroups(); }, 30000);
    const onFocus = () => { fetchVehicles(); fetchCustomers(); fetchGroups(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchVehicles = async () => {
    try {
      const response = await authFetch(`${API}/vehicles`, {
      });
      if (!response.ok) throw new Error('Failed to fetch vehicles');
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await authFetch(`${API}/customers`);
      if (res.ok) setCustomers(await res.json());
    } catch {}
  };

  const fetchGroups = async () => {
    try {
      const res = await authFetch(`${API}/vehicle-groups`);
      if (res.ok) setGroups(await res.json());
    } catch {}
  };

  const getGroupName = (gid) => {
    const g = groups.find(gr => gr.group_id === gid);
    return g ? g.name : null;
  };

  // Build a map: vehicle_id -> customer
  const vehicleCustomerMap = {};
  customers.forEach(c => {
    (c.vehicle_ids || []).forEach(vid => {
      vehicleCustomerMap[vid] = c;
    });
  });

  const getVehicleCustomer = (vid) => vehicleCustomerMap[vid] || null;

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    const url = editingGroup ? `${API}/vehicle-groups/${editingGroup.group_id}` : `${API}/vehicle-groups`;
    const method = editingGroup ? 'PUT' : 'POST';
    try {
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: groupForm.name, price: groupForm.price === '' ? 0 : Number(groupForm.price), cost: groupForm.cost === '' ? 0 : Number(groupForm.cost) })
      });
      if (!res.ok) throw new Error();
      const msg = editingGroup ? 'Group updated — price/cost applied to all vehicles in group' : 'Group created';
      toast.success(msg);
      setGroupDialogOpen(false);
      setEditingGroup(null);
      setGroupForm({ name: '', price: '', cost: '' });
      fetchGroups();
      fetchVehicles();
    } catch { toast.error('Failed to save group'); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"? Vehicles will be unassigned.`)) return;
    try {
      const res = await authFetch(`${API}/vehicle-groups/${group.group_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('Group deleted');
      fetchGroups();
      fetchVehicles();
    } catch { toast.error('Failed to delete group'); }
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, price: group.price || '', cost: group.cost || '' });
    setGroupDialogOpen(true);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingVehicle
        ? `${API}/vehicles/${editingVehicle.vehicle_id}`
        : `${API}/vehicles`;
      const method = editingVehicle ? 'PUT' : 'POST';

      const { customer_id, group_id, ...vehicleData } = formData;

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...vehicleData, group_id: group_id || null })
      });

      if (!response.ok) throw new Error('Failed to save vehicle');

      const savedVehicle = await response.json();
      const vid = savedVehicle.vehicle_id;

      // Update customer assignment
      const oldCust = getVehicleCustomer(editingVehicle?.vehicle_id);
      const newCustId = customer_id;

      // Remove from old customer if changed
      if (oldCust && oldCust.customer_id !== newCustId) {
        const updatedIds = (oldCust.vehicle_ids || []).filter(id => id !== vid);
        await authFetch(`${API}/customers/${oldCust.customer_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...oldCust, vehicle_ids: updatedIds })
        });
      }

      // Add to new customer if set
      if (newCustId && (!oldCust || oldCust.customer_id !== newCustId)) {
        const newCust = customers.find(c => c.customer_id === newCustId);
        if (newCust) {
          const updatedIds = [...new Set([...(newCust.vehicle_ids || []), vid])];
          await authFetch(`${API}/customers/${newCustId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...newCust, vehicle_ids: updatedIds })
          });
        }
      }

      toast.success(editingVehicle ? 'Vehicle updated' : 'Vehicle added');
      setDialogOpen(false);
      setEditingVehicle(null);
      setFormData({ unit_number: '', license_plate: '', make: '', model: '', customer_id: '' });
      fetchVehicles();
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    const cust = getVehicleCustomer(vehicle.vehicle_id);
    setFormData({
      unit_number: vehicle.unit_number || '',
      license_plate: vehicle.license_plate || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      customer_id: cust ? cust.customer_id : '',
      group_id: vehicle.group_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      const response = await authFetch(`${API}/vehicles/${vehicleId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete vehicle');

      toast.success('Vehicle deleted');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const handleExport = async () => {
    try {
      const response = await authFetch(`${API}/export/vehicles`, {
      });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fleet_vehicles.xlsx';
      a.click();
      toast.success('Exported successfully');
    } catch (error) {
      toast.error('Export failed');
    }
  };




  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await authFetch(`${API}/import/vehicles`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }

      const result = await response.json();
      toast.success(result.message);
      fetchVehicles();
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/template/vehicles`, '_blank');
  };

  const updateBatchRow = (index, field, value) => {
    setBatchRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleBatchSubmit = async () => {
    const valid = batchRows.filter(r => r.unit_number.trim() !== '');
    if (valid.length === 0) {
      toast.error('Add at least one vehicle with a Unit #');
      return;
    }
    try {
      const payload = valid.map(r => ({
        unit_number: r.unit_number,
        license_plate: r.license_plate,
        make: r.make,
        model: r.model
      }));
      const response = await authFetch(`${API}/vehicles/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Batch add failed');
      toast.success(`${valid.length} vehicle(s) added`);
      setBatchOpen(false);
      setBatchRows([{ ...emptyRow }, { ...emptyRow }, { ...emptyRow }]);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to add vehicles');
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || (
      (vehicle.unit_number && vehicle.unit_number.toLowerCase().includes(query)) ||
      (vehicle.license_plate && vehicle.license_plate.toLowerCase().includes(query)) ||
      (vehicle.make && vehicle.make.toLowerCase().includes(query)) ||
      (vehicle.model && vehicle.model.toLowerCase().includes(query))
    );
    const matchesCustomer = customerFilter === 'all' ||
      (customerFilter === 'unassigned' && !getVehicleCustomer(vehicle.vehicle_id)) ||
      (getVehicleCustomer(vehicle.vehicle_id)?.customer_id === customerFilter);
    const matchesGroup = groupFilter === 'all' ||
      (groupFilter === 'ungrouped' && !vehicle.group_id) ||
      (vehicle.group_id === groupFilter);
    return matchesSearch && matchesCustomer && matchesGroup;
  });

  return (
    <Layout user={user}>
      <div className="p-4 lg:p-12 space-y-6 lg:space-y-8 bg-slate-200 min-h-screen">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="fleet-title">Fleet Management</h1>
            <p className="text-slate-600 text-sm lg:text-base mt-1 lg:mt-2">Manage your vehicle inventory</p>
          </div>
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <Button variant="outline" onClick={handleExport} data-testid="export-button" className="text-xs lg:text-sm">
              <Download className="h-4 w-4 mr-1 lg:mr-2" />
              EXPORT
            </Button>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-button" className="text-xs lg:text-sm" disabled={importing}>
              <Upload className="h-4 w-4 mr-1 lg:mr-2" />
              {importing ? 'IMPORTING...' : 'IMPORT'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleImport}
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingVehicle(null);
                setFormData({ unit_number: '', license_plate: '', make: '', model: '', customer_id: '', group_id: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-vehicle-button">
                  <Plus className="h-4 w-4 mr-2" />
                  ADD VEHICLE
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">
                    {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Unit #</Label>
                    <Input
                      data-testid="vehicle-unit-input"
                      value={formData.unit_number}
                      onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                      placeholder="e.g., UNIT-001"
                      required
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">License Plate #</Label>
                    <Input
                      data-testid="vehicle-license-input"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                      placeholder="e.g., ABC-1234"
                      required
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Make</Label>
                    <Input
                      data-testid="vehicle-make-input"
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="e.g., Ford"
                      required
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Model</Label>
                    <Input
                      data-testid="vehicle-model-input"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., Transit"
                      required
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  {customers.length > 0 && (
                    <div className="space-y-2">
                      <Label className="uppercase text-xs font-bold tracking-wide">Customer</Label>
                      <Select value={formData.customer_id || 'none'} onValueChange={(v) => setFormData({ ...formData, customer_id: v === 'none' ? '' : v })}>
                        <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="vehicle-customer-select">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Customer</SelectItem>
                          {customers.map(c => (
                            <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="uppercase text-xs font-bold tracking-wide">Group</Label>
                    <Select value={formData.group_id || 'none'} onValueChange={(v) => setFormData({ ...formData, group_id: v === 'none' ? '' : v })}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="vehicle-group-select">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Group</SelectItem>
                        {groups.map(g => (
                          <SelectItem key={g.group_id} value={g.group_id}>{g.name} (${g.price}/{g.cost})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full uppercase font-bold" data-testid="vehicle-submit-button">
                    {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={() => setBatchOpen(true)} data-testid="batch-add-button" className="text-xs lg:text-sm">
              <Layers className="h-4 w-4 mr-1 lg:mr-2" />
              BATCH ADD
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search vehicles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200"
                data-testid="vehicle-search-input"
              />
            </div>
            {customers.length > 0 && (
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-full sm:w-[200px] bg-slate-50 border-slate-200" data-testid="customer-filter">
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger className="w-full sm:w-[200px] bg-slate-50 border-slate-200" data-testid="group-filter">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                <SelectItem value="ungrouped">Ungrouped</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(searchQuery || customerFilter !== 'all' || groupFilter !== 'all') && (
            <p className="text-sm text-slate-600 mt-2">
              Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Vehicle Groups */}
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Vehicle Groups</h2>
            <Button size="sm" variant="outline" onClick={() => { setEditingGroup(null); setGroupForm({ name: '', price: '', cost: '' }); setGroupDialogOpen(true); }} data-testid="add-group-button">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Group
            </Button>
          </div>
          {groups.length === 0 ? (
            <p className="text-sm text-slate-400">No groups yet. Create a group to set price/cost for multiple vehicles.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {groups.map(g => {
                const count = vehicles.filter(v => v.group_id === g.group_id).length;
                return (
                  <div key={g.group_id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid={`group-tag-${g.group_id}`}>
                    <span className="font-semibold">{g.name}</span>
                    <span className="text-slate-500">P: ${(g.price || 0).toFixed(2)}</span>
                    <span className="text-slate-500">C: ${(g.cost || 0).toFixed(2)}</span>
                    <span className="text-xs text-slate-400">({count})</span>
                    <button onClick={() => openEditGroup(g)} className="text-blue-500 hover:text-blue-700"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => handleDeleteGroup(g)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="vehicles-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Unit #</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">License Plate #</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Make</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Model</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Group</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                      {searchQuery ? `No vehicles found matching "${searchQuery}"` : 'No vehicles found'}
                    </td>
                  </tr>
                ) : (
                  filteredVehicles.map((vehicle, index) => (
                    <tr key={vehicle.vehicle_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`vehicle-row-${index}`}>
                      <td className="px-4 py-4 font-mono font-semibold text-sm">{vehicle.unit_number || '-'}</td>
                      <td className="px-4 py-4 font-mono text-sm">{vehicle.license_plate || '-'}</td>
                      <td className="px-4 py-4 text-sm">{vehicle.make || '-'}</td>
                      <td className="px-4 py-4 text-sm">{vehicle.model || '-'}</td>
                      <td className="px-4 py-4 text-sm">
                        {vehicle.group_id && getGroupName(vehicle.group_id)
                          ? <span className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded font-medium">{getGroupName(vehicle.group_id)}</span>
                          : <span className="text-slate-400 text-xs">-</span>
                        }
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {(() => {
                          const cust = getVehicleCustomer(vehicle.vehicle_id);
                          return cust
                            ? <span className="inline-block bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded font-medium">{cust.name}</span>
                            : <span className="text-slate-400 text-xs">-</span>;
                        })()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(vehicle)}
                            data-testid={`edit-vehicle-${index}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(vehicle.vehicle_id)}
                            data-testid={`delete-vehicle-${index}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={batchOpen} onOpenChange={(open) => {
          setBatchOpen(open);
          if (!open) setBatchRows([{ ...emptyRow }, { ...emptyRow }, { ...emptyRow }]);
        }}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">Batch Add Vehicles</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="hidden lg:grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                <span>Unit #</span><span>License Plate #</span><span>Make</span><span>Model</span><span></span>
              </div>
              {batchRows.map((row, i) => (
                <div key={i} className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_40px] gap-2 items-center border-b pb-3 lg:border-0 lg:pb-0" data-testid={`batch-row-${i}`}>
                  <Input placeholder="Unit #" value={row.unit_number} onChange={e => updateBatchRow(i, 'unit_number', e.target.value)} className="bg-slate-50 border-slate-200 text-sm" />
                  <Input placeholder="License Plate" value={row.license_plate} onChange={e => updateBatchRow(i, 'license_plate', e.target.value)} className="bg-slate-50 border-slate-200 text-sm" />
                  <Input placeholder="Make" value={row.make} onChange={e => updateBatchRow(i, 'make', e.target.value)} className="bg-slate-50 border-slate-200 text-sm" />
                  <Input placeholder="Model" value={row.model} onChange={e => updateBatchRow(i, 'model', e.target.value)} className="bg-slate-50 border-slate-200 text-sm" />
                  <Button variant="ghost" size="icon" onClick={() => setBatchRows(prev => prev.filter((_, idx) => idx !== i))} disabled={batchRows.length <= 1} data-testid={`batch-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2">
                <Button variant="outline" size="sm" onClick={() => setBatchRows(prev => [...prev, { ...emptyRow }])} data-testid="batch-add-row">
                  <Plus className="h-4 w-4 mr-1" /> Add Row
                </Button>
                <Button onClick={handleBatchSubmit} data-testid="batch-submit-button" className="uppercase font-bold">
                  Add {batchRows.filter(r => r.unit_number.trim()).length} Vehicle(s)
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={(open) => { setGroupDialogOpen(open); if (!open) { setEditingGroup(null); setGroupForm({ name: '', price: '', cost: '' }); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">{editingGroup ? 'Edit Group' : 'New Group'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGroupSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="uppercase text-xs font-bold tracking-wide">Group Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} required placeholder="e.g. Trucks, Sedans" className="bg-slate-50 border-slate-200" data-testid="group-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Price $</Label>
                <Input type="number" step="0.01" value={groupForm.price} onChange={(e) => setGroupForm({ ...groupForm, price: e.target.value })} placeholder="0.00" className="bg-slate-50 border-slate-200" data-testid="group-price-input" />
              </div>
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Cost $</Label>
                <Input type="number" step="0.01" value={groupForm.cost} onChange={(e) => setGroupForm({ ...groupForm, cost: e.target.value })} placeholder="0.00" className="bg-slate-50 border-slate-200" data-testid="group-cost-input" />
              </div>
            </div>
            {editingGroup && <p className="text-xs text-slate-500">Saving will apply this price/cost to all vehicles in this group.</p>}
            <Button type="submit" className="w-full uppercase font-bold" data-testid="group-submit-button">{editingGroup ? 'Save & Apply to All' : 'Create Group'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
